import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { fbLeadsSetup } from "@/services/api/cresivoxServer";
import { REDUCER_NAME, ISyncLeadsDTO, IUpdateLeadDTO } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const getLeads = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { status?: string; page?: number; limit?: number } | undefined,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getLeads"),
  async (params, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.getLeads(params?.status, params?.page, params?.limit);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const syncLeads = createAsyncThunk<
  iGenericResponse<any | null> | null,
  ISyncLeadsDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("syncLeads"),
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.syncLeads(payload.formId);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const updateLead = createAsyncThunk<
  iGenericResponse<any | null> | null,
  IUpdateLeadDTO,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateLead"),
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.updateLead(id, payload);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, id, ...payload, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const importLeads = createAsyncThunk<
  iGenericResponse<any | null> | null,
  File,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("importLeads"),
  async (file, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.importLeads(file);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getLeadForms = createAsyncThunk<
  iGenericResponse<any | null> | null,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getLeadForms"),
  async (_, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.getLeadForms();
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getLeadById = createAsyncThunk<
  iGenericResponse<any | null> | null,
  string,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getLeadById"),
  async (id, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.getLeadById(id);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const addActivity = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { id: string; type: string; description: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addActivity"),
  async ({ id, type, description }, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.addActivity(id, type, description);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_CREATED) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const updateLeadFull = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { id: string; fullName?: string; email?: string; phone?: string; status?: string; notes?: string },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("updateLeadFull"),
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.updateLead(id, payload);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, id, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const deleteLead = createAsyncThunk<
  iGenericResponse<any | null> | null,
  string,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("deleteLead"),
  async (id, { rejectWithValue }) => {
    try {
      const response = await fbLeadsSetup.deleteLead(id);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, id, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: "No response received" });
    } catch {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);
